'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { X, ArrowRight } from 'lucide-react';
import { trackCtaClick } from '@/lib/analytics';

interface StickyMobileCTAProps {
  /** Text to display */
  text?: string;
  /** Link destination */
  href: string;
  /** Service name for tracking */
  serviceName?: string;
  /** Scroll threshold before showing (default: 400px) */
  scrollThreshold?: number;
  /** Analytics location identifier */
  ctaLocation?: 'kb_article' | 'blog_post' | 'tool_page';
  /** Show on desktop as well (default: false for backwards compatibility) */
  showOnDesktop?: boolean;
}

/**
 * Sticky CTA
 *
 * A non-intrusive CTA that appears after scrolling:
 * - Mobile: Bottom bar across full width
 * - Desktop (when enabled): Bottom-right floating button
 */
export default function StickyMobileCTA({
  text = 'Need expert help?',
  href,
  serviceName,
  scrollThreshold = 400,
  ctaLocation = 'blog_post',
  showOnDesktop = false,
}: StickyMobileCTAProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (isDismissed) return;

      const scrollY = window.scrollY;
      const shouldShow = scrollY > scrollThreshold;

      // Also hide when near the bottom (where the main CTA is)
      const nearBottom = window.innerHeight + scrollY >= (document.body?.offsetHeight ?? document.documentElement.scrollHeight) - 500;

      setIsVisible(shouldShow && !nearBottom);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial position

    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrollThreshold, isDismissed]);

  const handleClick = () => {
    trackCtaClick(text, ctaLocation as 'inline', href);
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDismissed(true);
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Mobile: Bottom bar */}
      <div
        className="lg:hidden fixed bottom-0 left-0 right-0 z-50"
        style={{
          animation: 'slideUp 0.3s ease-out',
        }}
      >
        <style jsx>{`
          @keyframes slideUp {
            from {
              transform: translateY(100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}</style>

        <div className="bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] shadow-lg">
          <div className="flex items-center justify-between px-4 py-3">
            <Link
              href={href}
              onClick={handleClick}
              className="flex-1 flex items-center justify-center gap-2 text-white font-medium"
            >
              <span>{text}</span>
              <span className="text-white/90">→</span>
              {serviceName && (
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                  {serviceName}
                </span>
              )}
            </Link>

            <button
              onClick={handleDismiss}
              className="ml-2 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-white/80 hover:text-white active:bg-white/10 rounded-md transition-colors"
              aria-label="Dismiss notification"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Desktop: Floating button (bottom-right) */}
      {showOnDesktop && (
        <div
          className="hidden lg:block fixed bottom-6 right-6 z-50"
          style={{
            animation: 'slideInRight 0.3s ease-out',
          }}
        >
          <div className="relative group">
            <Link
              href={href}
              onClick={handleClick}
              className="flex items-center gap-3 bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] text-white px-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              <span className="font-semibold">{text}</span>
              {serviceName && (
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                  {serviceName}
                </span>
              )}
              <ArrowRight className="w-5 h-5" />
            </Link>

            <button
              onClick={handleDismiss}
              className="absolute -top-2 -right-2 bg-gray-800 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-700"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
